import { useEffect, useId, useRef } from "react";
import mascot from "@/assets/img/icon_aimoge.webp";
import {
  type DockingOnboardingWallProps,
  type DockingPresentation,
  useDockingPresenter,
} from "@/features/mix-onboarding/useDockingPresenter";
import { BoardSelectionCards } from "@/features/settings/components/sections/BoardSelectionCards";
import { ComponentScope } from "@/shared/ui/component-hierarchy/ComponentScope";
import { Button, Toggle } from "@/shared/ui/form";
import { getDialogAccessibilityProps } from "@/shared/ui/overlay/dialogAccessibility";
import { useDialogFocusTrap } from "@/shared/ui/overlay/dialogFocus";

export const DockingOnboardingWall: React.FunctionComponent<
  DockingOnboardingWallProps
> = (props: DockingOnboardingWallProps) => {
  const presentation = useDockingPresenter(props);
  return (
    <ComponentScope node={presentation.node}>
      <DockingOnboardingView
        {...presentation}
        registeredSlugs={props.registeredSlugs}
      />
    </ComponentScope>
  );
};

type DockingOnboardingViewProps = Omit<
  DockingPresentation,
  "node" | "completed"
> & { registeredSlugs: readonly string[] };
export const DockingOnboardingView: React.FunctionComponent<
  DockingOnboardingViewProps
> = ({
  registeredSlugs,
  step,
  isAdult,
  hasVisitedBoards,
  isInstalling,
  installError,
  installPromptState,
  onEvent,
}: DockingOnboardingViewProps) => {
  const titleId = useId();
  const titleRef = useRef<HTMLHeadingElement | HTMLParagraphElement>(null);
  const previousStepRef = useRef(step);
  const dialogRef = useDialogFocusTrap<HTMLElement>(true);
  useEffect(() => {
    if (previousStepRef.current === step) return;
    previousStepRef.current = step;
    titleRef.current?.focus({ preventScroll: true });
  }, [step]);
  return (
    <section
      ref={dialogRef}
      {...getDialogAccessibilityProps(titleId, true)}
      className="fixed inset-0 z-50 overflow-y-auto overscroll-contain bg-background"
    >
      <article
        aria-labelledby={titleId}
        className="mx-auto flex min-h-full w-full max-w-2xl flex-col justify-center gap-4 px-4 py-8"
      >
        {/* 狭幅では折り返して横スクロールを出さない(4文字×60pxで320px級を超えるため) */}
        <p className="flex flex-wrap items-center justify-center gap-2.5 text-6xl font-bold text-foreground">
          <img
            src={mascot}
            alt=""
            aria-hidden="true"
            className="h-[5.625rem] w-[5.625rem]"
          />
          あいもげ
        </p>
        {step === "age" ? (
          <>
            <h1
              ref={titleRef as React.RefObject<HTMLHeadingElement>}
              id={titleId}
              tabIndex={-1}
              className="text-lg font-bold text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            >
              あなたは18歳以上ですか？
            </h1>
            <p className="text-sm text-muted-foreground">
              「はい」を選ぶと、R-18指定の板に入れるようになります。
            </p>
            <fieldset className="m-0 flex items-center justify-center gap-3 rounded-lg border-0 bg-card px-4 py-4">
              <legend className="sr-only">年齢確認</legend>
              <b className="text-sm font-bold text-foreground">いいえ</b>
              <Toggle
                checked={isAdult}
                onChange={(isAdult: boolean): void =>
                  onEvent({ type: "docking.age-changed", isAdult })
                }
                aria-label="18歳以上である"
              />
              <b className="text-sm font-bold text-foreground">はい</b>
            </fieldset>
            {isAdult ? (
              <Button
                variant="primary"
                onClick={(): void =>
                  onEvent({ type: "docking.next", fromStep: step })
                }
              >
                次へ
              </Button>
            ) : hasVisitedBoards ? (
              <Button
                variant="secondary"
                onClick={(): void => onEvent({ type: "docking.back" })}
              >
                板選択に戻る
              </Button>
            ) : (
              <p role="status" className="text-sm text-destructive">
                現在R-18の板だけで構成されているため、ご利用いただけません
              </p>
            )}
          </>
        ) : step === "boards" ? (
          <>
            <h1
              ref={titleRef as React.RefObject<HTMLHeadingElement>}
              id={titleId}
              tabIndex={-1}
              className="text-lg font-bold text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            >
              表示項目を選んで下さい
            </h1>
            <p className="text-sm text-muted-foreground">
              選んだ板のスレを1つのカタログへ混ぜて表示します。
              <br />
              あとから設定で変更できます。
            </p>
            <BoardSelectionCards
              registeredSlugs={registeredSlugs}
              className="rounded-lg bg-card"
            />
            <Button
              variant="primary"
              onClick={(): void =>
                onEvent({ type: "docking.next", fromStep: step })
              }
            >
              次へ
            </Button>
          </>
        ) : (
          <div data-nosnippet className="flex flex-col gap-4">
            <p
              ref={titleRef as React.RefObject<HTMLParagraphElement>}
              id={titleId}
              tabIndex={-1}
              className="text-lg font-bold text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            >
              あいもげをアプリに追加しよう！
            </p>
            <p className="text-sm text-muted-foreground">
              ホーム画面から総合カタログをすぐに開けて、アプリのように使えます。
            </p>
            {installPromptState === "available" || isInstalling ? (
              <Button
                variant="primary"
                disabled={isInstalling}
                onClick={(): void => {
                  onEvent({ type: "docking.install" });
                }}
              >
                {isInstalling ? "追加画面を開いています…" : "アプリを追加"}
              </Button>
            ) : (
              <p className="rounded-lg bg-card px-4 py-3 text-sm text-foreground">
                追加できるブラウザでは、共有メニューまたはブラウザメニューから
                「ホーム画面に追加」「アプリをインストール」を選んでください。
              </p>
            )}
            <p
              role="status"
              aria-atomic="true"
              className="text-sm text-destructive"
            >
              {installError
                ? "追加画面を開けませんでした。ブラウザのメニューから追加してください。"
                : ""}
            </p>
            <Button
              variant="secondary"
              onClick={(): void => onEvent({ type: "docking.skip" })}
            >
              今はしない
            </Button>
          </div>
        )}
      </article>
    </section>
  );
};
